// client/src/features/notifications/search.ts — the URL state of SCR-NOTIF-001 and SCR-ACT-001
// · ALR-004 (04 §9.2.3, §8.12).
//
// §9.2.3 is normative for every LIST/BOARD screen: filters and pagination live in the URL as
// Zod-validated search params, so a filtered list is shareable, survives refresh, and Back is a
// filter-undo. 04 §8.12 names the Action Center's three by hand — „filters (property, type,
// status — search params)" — and they are the endpoint's own three, unrenamed.
//
// NEITHER SCHEMA HAS A `sort`. Both endpoints document their order as fixed and refuse to take
// it from the query string: the inbox is „unread first, then CRITIC first, then newest", the
// Action Center is „priority → due date → age" (04 §8.12). A `sort` in the URL would be a
// promise the API does not keep — AUD-002 reached the same conclusion for the same reason.
//
// `taskKind` is a plain trimmed string and not a `z.enum(TASK_KINDS)`, because `task_kind` is a
// REGISTRY column and not an enum (03 §15.4): a kind added server-side without a migration must
// still round-trip through a shared link. An unknown value narrows the list to nothing, which
// is what any filter for something absent does.
//
// ── The status filter became a tab, and a deadline filter joined it (D-2560, D-2561) ─────
// §8.12's „status" filter was a select of the four states, and its empty choice „Toate" opened
// the screen on every task ever raised, history first-class beside the work. The question the
// screen answers is „what is left to do", so the lifecycle is now a TAB defaulting to the
// waiting tasks (`open=true` on the wire), with the closed ones and the whole history one tab
// away. `due` is the tile the user pressed; it only means something on the waiting tab, so
// changing tab drops it. A shared link carrying the retired `status=` is stripped by the schema
// and lands on the default tab rather than on a filter the screen no longer shows.

import { z } from 'zod';

import { TASK_DUE_WINDOWS } from './enums';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
const paging = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
};

/**
 * SCR-NOTIF-001. One filter only: 04 §8.12 gives the inbox „priority-grouped … read state" and
 * no filter row, and „doar necitite" is the one narrowing a reader actually asks for — it is
 * also `GET /api/v1/notifications`'s own `unreadOnly`, so nothing is renamed on the way out.
 */
export const notificationSearchSchema = z.object({
  ...paging,
  unreadOnly: z.coerce.boolean().optional(),
});

export type NotificationSearch = z.infer<typeof notificationSearchSchema>;

/** The three tabs of SCR-ACT-001 (D-2560): the waiting tasks, the closed ones, all of them. */
export const TASK_VIEWS = ['open', 'closed', 'all'] as const;

export type TaskView = (typeof TASK_VIEWS)[number];

/**
 * SCR-ACT-001. 04 §8.12's three filters — property, type, and status as the tab — plus the
 * deadline window a tile applies, spelled as `GET /api/v1/task-items` spells them where it can.
 */
export const actionCenterSearchSchema = z.object({
  ...paging,
  view: z.enum(TASK_VIEWS).default('open'),
  due: z.enum(TASK_DUE_WINDOWS).optional(),
  propertyId: z.string().uuid().optional(),
  taskKind: z.string().trim().optional(),
});

export type ActionCenterSearch = z.infer<typeof actionCenterSearchSchema>;

/** The tab as the endpoint's `open` flag: true, false, or absent for every task. */
export function openFlagOf(view: TaskView): boolean | undefined {
  if (view === 'open') return true;
  if (view === 'closed') return false;
  return undefined;
}

/**
 * Which filters are engaged — the empty state needs to know why it is empty, and „nu ai nimic
 * de făcut" is a very different sentence from „niciun rezultat pentru filtrele astea". Getting
 * that wrong on THIS screen is the worst version of the mistake: 01 BR-NOTIF-07 makes „a user
 * who does everything the Action Center says reaches an empty list" a promise of the product.
 */
export function hasActiveTaskFilters(search: ActionCenterSearch): boolean {
  return (
    search.propertyId !== undefined ||
    (search.taskKind !== undefined && search.taskKind !== '') ||
    search.due !== undefined
  );
}

/**
 * Clearing the filters returns to page 1 and keeps the tab and the page size the user chose —
 * the tab is where they are, not a filter they set.
 */
export function clearedTaskSearch(search: ActionCenterSearch): ActionCenterSearch {
  return { page: 1, pageSize: search.pageSize, view: search.view };
}
